import { get, has, set, unset } from 'lodash';
import { stringify } from 'qs';

import { Many } from './many';

export type SerializableParamValue =
  | undefined
  | boolean
  | number
  | string
  | boolean[]
  | number[]
  | string[]
  | ISerializableParam
  | ISerializableParam[];

/**
 * URL parameter value types that can be represented in a string format.
 */
export interface ISerializableParam {
  [key: string]: SerializableParamValue;
}

export type ParamName = string | number | symbol;
export type ParamPath = Many<ParamName>;

/**
 * Class for creating `application/x-www-form-urlencoded` serialized data using percent encoding.
 *
 * Usage:
 *     new SerializableParams({ key: value }).toString()
 *
 * NOTE: this is an alternative to URLSearchParams, which uses a different encoding scheme.
 *
 * @see URLSearchParams
 * @see https://url.spec.whatwg.org/#interface-urlsearchparams
 * @see https://url.spec.whatwg.org/#application/x-www-form-urlencoded
 * @see https://developer.mozilla.org/en-US/docs/Glossary/percent-encoding
 */
export class SerializableParams {
  public get [Symbol.toStringTag](): string {
    return 'SerializableParams';
  }

  constructor(private values: ISerializableParam = {}) {
  }

  // tslint:disable-next-line:typedef
  public [Symbol.toPrimitive](hint: 'default' | 'string' | 'number') {
    if (hint === 'number') {
      return NaN;
    }
    return this.stringifyValues();
  }

  public has(path: ParamPath): boolean {
    return has(this.values, path);
  }

  public get<V extends SerializableParamValue>(path: ParamPath): V | undefined {
    return get(this.values, path) as unknown as V;
  }

  public set(path: ParamPath, value: SerializableParamValue): void {
    set(this.values, path, value);
  }

  public unset(path: ParamPath): void {
    unset(this.values, path);
  }

  public toString(): string {
    return this.stringifyValues();
  }

  public valueOf(): typeof NaN {
    return NaN;
  }

  private stringifyValues(): string {
    return stringify(this.values);
  }
}
